<template>
    <div class="h-100">
        <b-container class="p-0" fluid>
            <b-row align-h="center">
                <b-col cols="12" md="10">
                    <b-link :to="{ name: 'settings' }"
                        ><font-awesome-icon icon="arrow-left" />
                        {{ $t("Back") }}
                    </b-link>
                    <div class="d-flex align-items-center mt-1">
                        <h1>{{ $t("mainmenu.settings") }} / {{ $t("settings.ip-whitelisting.IP whitelisting") }}</h1>
                        <b-button id="popover-info" class="ml-1 p-0 info-square" size="sm" variant="primary"
                            >i</b-button
                        >
                    </div>

                    <b-popover
                        :show.sync="popoverShow"
                        placement="bottom"
                        target="popover-info"
                        triggers="click blur"
                        variant="white"
                    >
                        <template #default>
                            <b-button
                                aria-label="secondary"
                                class="px-50 py-25 popover-close"
                                variant="light"
                                @click="onPopoverClose"
                            >
                                <span aria-hidden="true">&times;</span>
                            </b-button>
                            <h4>{{ $t("settings.ip-whitelisting.IP whitelisting") }}</h4>
                            <p class="text-secondary mb-0">
                                <small>{{ $t("settings.ip-whitelisting.intro1") }}</small>
                            </p>
                        </template>
                    </b-popover>

                    <div class="mt-2">
                        <b-alert class="p-50 d-flex" show variant="warning">
                            <font-awesome-icon class="mr-50" icon="exclamation-triangle" size="lg" />
                            <span>{{ $t("settings.ip-whitelisting.notice") }}</span>
                        </b-alert>
                    </div>

                    <div class="m-2">
                        <b-row align-h="between">
                            <b-col class="d-flex" cols="12" md="8">
                                <b-button
                                    id="contacts-add"
                                    :to="{ name: 'settings-ipwhitelisting-add' }"
                                    class="mr-2 text-nowrap"
                                    variant="primary"
                                >
                                    <font-awesome-icon class="mr-50" icon="plus" />
                                    <span>{{ $t("settings.ip-whitelisting.Add IP") }}</span>
                                </b-button>

                                <div class="d-flex align-items-center justify-content-end">
                                    <b-form-input
                                        v-model="searchQuery"
                                        :placeholder="$t('paginator.search')"
                                        class="d-inline-block mr-1 bg-light"
                                        debounce="500"
                                    />
                                </div>
                            </b-col>

                            <b-col cols="auto">
                                <label>{{ $t("paginator.show") }}</label>
                                <v-select
                                    v-model="perPage"
                                    :clearable="false"
                                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                    :options="perPageOptions"
                                    class="per-page-selector d-inline-block mx-50"
                                />
                                <label>{{ $t("paginator.entries") }}</label>
                            </b-col>
                        </b-row>
                    </div>

                    <b-table
                        ref="refTable"
                        :empty-text="$t('paginator.no-matching-records-found')"
                        :fields="tableColumns"
                        :items="localRead"
                        :no-sort-reset="true"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="isSortDirDesc"
                        class="position-relative"
                        primary-key="id"
                        responsive
                        show-empty
                    >
                        <!-- Column: Name -->
                        <template #cell(name)="data">
                            <strong>{{ data.item.name }}</strong>
                        </template>

                        <!-- Column: Actions -->
                        <template #cell(actions)="data">
                            <div class="text-right">
                                <b-button
                                    :to="{
                                        name: 'settings-ipwhitelisting-edit',
                                        params: { id: data.item.id },
                                    }"
                                    class="btn-icon mr-50"
                                    variant="light"
                                >
                                    <font-awesome-icon icon="pen" />
                                </b-button>
                                <b-button
                                    class="btn-icon mr-50"
                                    variant="light"
                                    @click="deleteOne({ id: data.item.id, name: data.item.name }, refetchData)"
                                >
                                    <font-awesome-icon icon="trash" />
                                </b-button>
                            </div>
                        </template>
                    </b-table>
                    <div class="mx-2 mb-2">
                        <b-row>
                            <b-col
                                class="d-flex align-items-center justify-content-center justify-content-sm-start"
                                cols="12"
                                sm="6"
                            >
                                <span class="text-muted">
                                    {{ $t("paginator.showing") }} {{ dataMeta.to }} {{ $t("paginator.of") }}
                                    {{ dataMeta.of }} {{ $t("paginator.entries") }}
                                </span>
                            </b-col>

                            <b-col
                                class="d-flex align-items-center justify-content-center justify-content-sm-end"
                                cols="12"
                                sm="6"
                            >
                                <b-pagination
                                    v-model="currentPage"
                                    :per-page="perPage"
                                    :total-rows="total"
                                    class="mb-0 mt-1 mt-sm-0"
                                    first-number
                                    last-number
                                    next-class="next-item"
                                    prev-class="prev-item"
                                    size="sm"
                                >
                                    <template #prev-text>
                                        <font-awesome-icon icon="chevron-left" size="lg" />
                                    </template>
                                    <template #next-text>
                                        <font-awesome-icon icon="chevron-right" size="lg" />
                                    </template>
                                </b-pagination>
                            </b-col>
                        </b-row>
                    </div>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
import {
    BAlert,
    BButton,
    BCard,
    BCol,
    BContainer,
    BFormInput,
    BLink,
    BPagination,
    BPopover,
    BRow,
    BTable,
} from "bootstrap-vue";
import useIpWhitelistingList from "./useIpWhitelistingList";
import vSelect from "vue-select";
import { ref } from "@vue/composition-api/dist/vue-composition-api";
import { useToast } from "vue-toastification/composition";
import i18n from "@/libs/i18n";

export default {
    components: {
        BContainer,
        BRow,
        BCol,
        BButton,
        BFormInput,
        BTable,
        BPagination,
        BCard,
        BAlert,
        BPopover,
        BLink,
        vSelect,
    },
    setup(_, context) {
        const toast = useToast();

        const {
            read,
            deleteOne,
            refetchData,
            resolvePlatformIcon,
            transformPlatformName,
            tableColumns,
            perPage,
            currentPage,
            total,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            refTable,
        } = useIpWhitelistingList(context.root.$bvModal);

        const localRead = (ctx, callback) => {
            read(ctx, callback).catch(toast.error(i18n.t("toast.read.error")));
        };

        /**
        // Popover info
         */
        const popoverShow = ref(false);

        const onPopoverClose = () => {
            popoverShow.value = false;
        };

        return {
            resolvePlatformIcon,
            transformPlatformName,
            tableColumns,
            perPage,
            currentPage,
            total,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            refTable,
            popoverShow,

            localRead,
            deleteOne,
            refetchData,
            onPopoverClose,
        };
    },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-select.scss";
</style>
